import React, { useEffect, useState } from 'react';
import Loader from "../../components/Loader"; 
import Paginator from "../../components/Paginator"; 
import ApiService from "../../services/ApiService";
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { Link, useSearchParams  } from 'react-router-dom';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { formatCurrency, formatDate, formatDateLocal } from "../../utils/helper";
import { getGenericStatus } from '../../utils/statusHelper';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';

const DescriptionText = ({ text, limit = 50 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    
    if (!text) return <span>-</span>;
    
    if (text.length <= limit) return <span>{text}</span>;

    return (
        <div>
            <span>{isExpanded ? text : `${text.substring(0, limit)}...`}</span>
            <button 
                className="btn btn-link btn-sm p-0 ms-1 align-baseline"
                onClick={() => setIsExpanded(!isExpanded)}
                style={{ fontSize: '12px', textDecoration: 'none' }}
            >
                {isExpanded ? 'View Less' : 'View More'}
            </button>
        </div>
    );
};

const List = () => {

    const [isLoader, setIsLoader] = useState(false);
    const [products, setProducts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [dateRange, setDateRange] = useState([null, null]);
    const [startDate, endDate] = dateRange;
    const [categories, setCategories] = useState([]);

    const [searchParams] = useSearchParams();

    const [apiData, setApiData] = useState({
        search: "",
        page: 1,
        start_date: "",
        end_date: "",
        category_id: "",
        lens_sub_category: "",
        lens_availability: "",
        lens_material: "",
        lens_product_type: "",
        lens_coating: "",
    });

    const selectedCategory = categories.find(c => c.id == apiData.category_id);
    const isLensSelected = selectedCategory && selectedCategory.name.toLowerCase() === 'lens';

    useEffect(() => {
        fetchCategories();
    }, []);

    useEffect(() => {
        getProducts();
    }, [apiData.page, apiData.search, apiData.start_date, apiData.end_date, apiData.category_id, apiData.lens_sub_category, apiData.lens_availability, apiData.lens_material, apiData.lens_product_type, apiData.lens_coating]);

    const fetchCategories = async () => {
        try {
            const response = await ApiService.request({
                method: 'GET',
                url: 'admin/product-categories?per_page=100'
            });
            if (response.data.status) {
                setCategories(response.data.data.categories);
            }
        } catch (error) {
            console.error('Failed to fetch categories', error);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setApiData(prev => {
            const updated = { ...prev, [name]: value, page: 1 };
            // Reset lens filters if category changes away from lens
            if (name === 'category_id') {
                updated.lens_sub_category = '';
                updated.lens_availability = '';
                updated.lens_material = '';
                updated.lens_product_type = '';
                updated.lens_coating = '';
            }
            return updated;
        });
    };

    const handleDateChange = (update) => {
        setDateRange(update);
        const [start, end] = update;
        setApiData(prev => ({
            ...prev,
            start_date: start ? formatDateLocal(start) : "",
            end_date: end ? formatDateLocal(end) : ""
        }));
    }; 

    const getProducts = async () => {
        try {
            setIsLoader(true);
            // Strip empty values so the backend doesn't receive blank filter params
            const params = Object.fromEntries(
                Object.entries(apiData).filter(([_, v]) => v !== "" && v !== null && v !== undefined)
            );
            const response = await ApiService.request({
                method: "GET",
                url: "admin/products",
                data: params
            });
            const data = response.data;
            if (data.status) {
                setProducts(data.data.products || []);
                setPagination(data.data.pagination || {});
            }
        } catch (error) {
            console.error(error);
            // toast.error("Failed to fetch products");
        } finally {
            setIsLoader(false);
        }
    };

    const handleDelete = async (id) => {
        Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const response = await ApiService.request({
                        method: "DELETE",
                        url: `admin/products/${id}`
                    });
                    if (response.data.status) {
                        toast.success(response.data.message);
                        getProducts();
                    } else {
                        toast.error(response.data.message);
                    }
                } catch (error) {
                    toast.error(error.response?.data?.message || "Failed to delete product");
                }
            }
        });
    };

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
                <div id="kt_app_toolbar_container" className="app-container container-fluid d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">
                            Product List
                        </h1>
                    </div>
                    <div className="d-flex align-items-center gap-2 gap-lg-3">
                        <OverlayTrigger placement="top" overlay={<Tooltip>Add Product</Tooltip>}>
                            <Link to="/products/create" className="btn btn-sm fw-bold btn-base">
                                Add Product
                            </Link>
                        </OverlayTrigger>
                    </div>
                </div>
            </div>

            <div id="kt_app_content" className="app-content flex-column-fluid">
                <div id="kt_app_content_container" className="app-container container-fluid">
                    <div className="card">
                        <div className="card-header border-0 pt-6">
                            <div className="card-title">
                                <div className="d-flex align-items-center position-relative my-1">
                                    <span className="svg-icon svg-icon-1 position-absolute ms-6">
                                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor" />
                                            <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor" />
                                        </svg>
                                    </span>
                                    <input
                                        type="text"
                                        data-kt-user-table-filter="search"
                                        className="form-control form-control-solid w-250px ps-14"
                                        placeholder="Search product"
                                        value={apiData.search}
                                        onChange={(e) => setApiData({ ...apiData, search: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="card-toolbar">
                                <div className="d-flex justify-content-end gap-3">
                                    <div className="w-200px">
                                         <DatePicker
                                            selectsRange={true}
                                            startDate={startDate}
                                            endDate={endDate}
                                            onChange={handleDateChange}
                                            className="form-control form-control-solid"
                                            placeholderText="Select Date Range"
                                            isClearable={true}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="card-header border-0 pb-4 pt-0">
                            <div className="w-100">
                                <div className="row g-3 align-items-end">
                                    <div className="col-md-2">
                                        <label className="form-label fw-semibold fs-7 text-muted mb-1">Category</label>
                                        <select
                                            name="category_id"
                                            className="form-select form-select-solid form-select-sm"
                                            value={apiData.category_id}
                                            onChange={handleFilterChange}
                                        >
                                            <option value="">All Categories</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {isLensSelected && (
                                        <>
                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold fs-7 text-muted mb-1">Sub Category</label>
                                                <select
                                                    name="lens_sub_category"
                                                    className="form-select form-select-solid form-select-sm"
                                                    value={apiData.lens_sub_category}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All</option>
                                                    <option value="Single Vision">Single Vision</option>
                                                    <option value="Bifocals">Bifocals</option>
                                                    <option value="Progressive">Progressive</option>
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold fs-7 text-muted mb-1">Availability</label>
                                                <select
                                                    name="lens_availability"
                                                    className="form-select form-select-solid form-select-sm"
                                                    value={apiData.lens_availability}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All</option>
                                                    <option value="Stock">Stock</option>
                                                    <option value="Order">Order</option>
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold fs-7 text-muted mb-1">Material</label>
                                                <select
                                                    name="lens_material"
                                                    className="form-select form-select-solid form-select-sm"
                                                    value={apiData.lens_material}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All</option>
                                                    <option value="CR39">CR39</option>
                                                    <option value="Polycarbonate">Polycarbonate</option>
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold fs-7 text-muted mb-1">Product Type</label>
                                                <select
                                                    name="lens_product_type"
                                                    className="form-select form-select-solid form-select-sm"
                                                    value={apiData.lens_product_type}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All</option>
                                                    <option value="Near">Near</option>
                                                    <option value="Distance">Distance</option>
                                                </select>
                                            </div>

                                            <div className="col-md-2">
                                                <label className="form-label fw-semibold fs-7 text-muted mb-1">Coating</label>
                                                <select
                                                    name="lens_coating"
                                                    className="form-select form-select-solid form-select-sm"
                                                    value={apiData.lens_coating}
                                                    onChange={handleFilterChange}
                                                >
                                                    <option value="">All</option>
                                                    <option value="HMC">HMC</option>
                                                    <option value="Transition">Transition</option>
                                                    <option value="Bluecut">Bluecut</option>
                                                    <option value="HMC Bluecut">HMC Bluecut</option>
                                                    <option value="HMC Transition">HMC Transition</option>
                                                    <option value="Bluecut Transition">Bluecut Transition</option>
                                                </select>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                        <div className="card-body py-4">
                            {isLoader ? (
                                <Loader />
                            ) : (
                                <div className="table-responsive">
                                    <table className="table align-middle table-row-dashed fs-6 gy-5">
                                        <thead>
                                            <tr className="text-start text-muted fw-bold fs-7 text-uppercase gs-0">
                                                <th className="min-w-125px">Product</th>
                                                <th className="min-w-125px">Category</th>
                                                <th className="min-w-150px">Description</th>
                                                <th className="min-w-125px">Price</th>
                                                <th className="min-w-125px">Stock</th>
                                                <th className="min-w-125px">Status</th>
                                                <th className="text-end min-w-100px">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody className="text-gray-600 fw-semibold">
                                            {products.map((product, index) => (
                                                <tr key={index}>
                                                    <td className="d-flex align-items-center">
                                                        <div className="symbol symbol-circle symbol-50px overflow-hidden me-3">
                                                            <div className="symbol-label">
                                                                <img src={product.image || "/assets/images/blank-image.png"} alt={product.name} className="w-100" />
                                                            </div>
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                            <Link to={`/products/edit/${product.id}`} className="text-gray-800 text-hover-primary mb-1">
                                                                {product.formatted_name}
                                                            </Link>
                                                            <span>{product.sku}</span>
                                                        </div>
                                                    </td>
                                                    <td>{product.category?.name || '-'}</td>
                                                    <td>
                                                        <DescriptionText text={product.description} limit={50} />
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-column">
                                                            <span>{formatCurrency(product.price)} / Pc</span>
                                                            <span className="text-muted fs-8">{formatCurrency(product.price * 2)} / Pair</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <div className="">
                                                            <div className="d-flex flex-column">
                                                                <span className='fw-bold'>{product.stock} Pcs</span>
                                                                <span className='text-muted fs-8'>({Math.floor(product.stock / 2)} {Math.floor(product.stock / 2) === 1 ? 'Pair' : 'Pairs'}{product.stock % 2 !== 0 ? ' + 1 Pc' : ''})</span>
                                                            </div>
                                                            {product.stock <= 0 ? (
                                                                <span className="badge badge-light-danger mt-1">Out of Stock</span>
                                                            ) : product.stock <= (product.alert_quantity || 5) ? (
                                                                <span className="badge badge-light-warning mt-1">Low Stock</span>
                                                            ) : null}
                                                        </div>
                                                    </td>
                                                    <td>
                                                        {(() => {
                                                            const { badgeClass, label } = getGenericStatus(product.status);
                                                            return (
                                                                <span className={`badge ${badgeClass}`}>
                                                                    {label}
                                                                </span>
                                                            );
                                                        })()}
                                                    </td>
                                                    <td className="text-end">
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Edit Product</Tooltip>}>
                                                            <Link to={`/products/edit/${product.id}`} className="btn btn-icon btn-light btn-active-light-primary btn-sm me-2">
                                                                <i className="fas fa-edit"></i>
                                                            </Link>
                                                        </OverlayTrigger>
                                                        <OverlayTrigger placement="top" overlay={<Tooltip>Delete Product</Tooltip>}>
                                                            <button onClick={() => handleDelete(product.id)} className="btn btn-icon btn-light btn-active-light-danger btn-sm">
                                                                <i className="fas fa-trash"></i>
                                                            </button>
                                                        </OverlayTrigger>
                                                    </td>
                                                </tr>
                                            ))}
                                            {products.length === 0 && (
                                                <tr>
                                                    <td colSpan="5" className="text-center">No products found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                            <Paginator pagination={pagination} onPageChange={(page) => setApiData({ ...apiData, page })} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default List;
